import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Eye, EyeSlash, ArrowRight, ArrowLeft } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { fmtApiError } from "@/lib/api";

const AUTH_TIDS = {
  loginPage:     "login-page",
  loginEmail:    "login-email",
  loginPassword: "login-password",
  loginSubmit:   "login-form-submit-button",
  loginError:    "login-error",
  loginShowPw:   "login-show-pw",
};

export default function LoginPage() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  React.useEffect(() => {
    if (user) {
      const to = location.state?.from || "/dashboard";
      navigate(to, { replace: true });
    }
  }, [user, location.state, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      navigate(location.state?.from || "/dashboard", { replace: true });
    } catch (err) {
      setError(fmtApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink-950 text-white relative overflow-hidden gold-aurora grain flex items-center justify-center px-6">
      <Link to="/" className="absolute top-6 left-6 inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition">
        <ArrowLeft size={14} /> Back to site
      </Link>

      <div data-testid={AUTH_TIDS.loginPage} className="w-full max-w-md glass relative z-10">
        <div className="p-8">
          <div className="mb-8">
            <span className="heading text-2xl font-extrabold tracking-tighter">
              DADDYOF<span className="text-gold-grad">.AI</span>
            </span>
            <h1 className="heading text-3xl font-bold tracking-tighter mt-6">Sign in</h1>
            <p className="text-zinc-400 text-sm mt-2">
              One device per account. New login auto-signs-out the previous device.
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <label className="overline mb-2 block">Email</label>
              <input
                data-testid={AUTH_TIDS.loginEmail}
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-black/40 border border-white/10 px-4 py-3 mono text-sm focus:border-gold/60 focus:outline-none transition"
                placeholder="you@desk.com"
              />
            </div>
            <div>
              <label className="overline mb-2 block">Password</label>
              <div className="relative">
                <input
                  data-testid={AUTH_TIDS.loginPassword}
                  type={showPw ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-black/40 border border-white/10 px-4 py-3 mono text-sm focus:border-gold/60 focus:outline-none transition pr-12"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  data-testid={AUTH_TIDS.loginShowPw}
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                  aria-label={showPw ? "Hide password" : "Show password"}
                >
                  {showPw ? <EyeSlash size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {error && (
              <div data-testid={AUTH_TIDS.loginError} className="text-sm text-bear bg-bear/10 border border-bear/30 px-4 py-3">
                {error}
              </div>
            )}

            <button
              type="submit"
              data-testid={AUTH_TIDS.loginSubmit}
              disabled={submitting}
              className="w-full inline-flex items-center justify-center gap-2 bg-gold text-black px-6 py-3 font-medium hover:bg-yellow-400 transition disabled:opacity-50"
            >
              {submitting ? "Signing in…" : (<>Enter Terminal <ArrowRight size={16} /></>)}
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-white/10 text-sm text-zinc-400">
            No account?{" "}
            <Link to="/signup" className="text-gold hover:text-yellow-400 underline-offset-2 hover:underline">
              Request access
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
